import { Box, Stack, styled, Typography } from '@chronosphereio/chrono-ui';
import { getErrorMessageFromUnknown, ProcessedError } from '@/core';

export interface VizContainerContentErrorProps {
  /**
   * Error can be one of the following:
   * - A single `Error`. Useful in cases like error boundaries.
   * - An array of processed errors. This handles the returned errors for data
   *   queries.
   */
  error: Error | ProcessedError[];
}

/**
 * Component used to render errors within `VizContainer.Body`. It is automatically used as the fallback component in
 * `VizContainer.Body` and `VizContainer.VizErrorBoundary`.
 *
 * Useful for cases where the panel is completely broken. For partial breakage cases, see error handling in
 * `VizContainer.Header`.
 */
export const VizContainerContentError = ({ error }: VizContainerContentErrorProps) => {
  // No errors, return nothing.
  if (!error) {
    return null;
  }

  // Normalize into an array.
  const errors = Array.isArray(error) ? error : [error];

  // No errors, return nothing.
  if (!errors.length) {
    return null;
  }

  return (
    <ErrorContainer>
      <ErrorList spacing={1.5} component="ul">
        {errors.map((error, i) => {
          const errorMessage = getErrorMessageFromUnknown(error);

          return (
            <ErrorItem
              component="li"
              // Including index in key because it is possible to have duplicate error messages.
              key={`${errorMessage}_${i}`}
            >
              {errorMessage}
            </ErrorItem>
          );
        })}
      </ErrorList>
    </ErrorContainer>
  );
};

const ErrorContainer = styled(Box)(() => {
  return {
    textAlign: 'center',
    minHeight: '100%',
    width: '100%',
    display: 'flex',
  };
});

const ErrorList = styled(Stack)(() => {
  return { textAlign: 'center', width: '100%', margin: 'auto', padding: 0 };

  // Type casting needed because of the `component` prop, styled, and typescript
  // not playing nicely together.
  // https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop
}) as typeof Stack;

const ErrorItem = styled(Typography)(({ theme }) => {
  return {
    color: theme.palette.error.main,
    listStyle: 'none',
  };
});
